import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { CourierOverviewRow, CourierShift } from "../api/types";
import { buildMonthGrid, MonthCalendar } from "../components/MonthCalendar";
import { ReasonModal } from "../components/ui/ReasonModal";

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthLabel(month: Date): string {
  return month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

const STATUS_DOT: Record<CourierShift["status"], string> = {
  PLANNED: "is-planned",
  CONFIRMED: "is-confirmed",
  ENDED: "is-ended",
  MISSED: "is-missed",
  CANCELLED: "is-cancelled",
};

const STATUS_LABEL: Record<CourierShift["status"], string> = {
  PLANNED: "Planned",
  CONFIRMED: "Confirmed",
  ENDED: "Ended",
  MISSED: "Missed",
  CANCELLED: "Withdrawn",
};

type PendingAction =
  | { kind: "plan"; date: string }
  | { kind: "cancel"; shift: CourierShift };

/**
 * G-2 owner plane: plan any courier's days on a month calendar. Reached from
 * the Couriers page ("Plan days"). The courier confirms each day from their
 * phone; unconfirmed planned days show up as the no-show signal in dispatch.
 */
export function CourierPlanningPage() {
  const { courierId } = useParams<{ courierId: string }>();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [shifts, setShifts] = useState<CourierShift[]>([]);
  const [courier, setCourier] = useState<CourierOverviewRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => {
    let stale = false;
    void api
      .couriersOverview()
      .then((rows) => {
        if (!stale) setCourier(rows.find((r) => r.id === courierId) ?? null);
      })
      .catch(() => {
        /* the calendar still works without the header row */
      });
    return () => {
      stale = true;
    };
  }, [courierId]);

  const refresh = useCallback(async () => {
    if (!courierId) return;
    const cells = buildMonthGrid(month);
    try {
      const list = await api.planningListShifts({
        from: isoDate(cells[0].date),
        to: isoDate(cells[cells.length - 1].date),
        courierUserId: courierId,
      });
      setShifts(list);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load the calendar");
    }
  }, [courierId, month]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const byDate = useMemo(() => {
    const map = new Map<string, CourierShift>();
    for (const shift of shifts) {
      if (shift.status !== "CANCELLED") map.set(shift.date, shift);
    }
    return map;
  }, [shifts]);

  const todayIso = isoDate(new Date());

  const run = async (action: () => Promise<unknown>, failMessage: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
      setPending(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : failMessage);
    } finally {
      setBusy(false);
    }
  };

  const onDayTap = (date: Date) => {
    const iso = isoDate(date);
    if (iso < todayIso) return;
    const shift = byDate.get(iso);
    if (!shift) {
      setPending({ kind: "plan", date: iso });
    } else if (shift.status === "PLANNED" || shift.status === "CONFIRMED") {
      setPending({ kind: "cancel", shift });
    }
  };

  return (
    <section className="page-stack courier-calendar">
      <div className="page-header">
        <div>
          <h2 className="page-title">
            Plan days{courier ? ` — ${courier.fullName}` : ""}
          </h2>
          <p className="page-subtitle">
            Tap a day to plan it for this courier; tap a planned day to cancel
            it. The courier confirms each day from their phone — an unconfirmed
            day past its start is your no-show signal.
          </p>
        </div>
        <Link to="/app/couriers" className="button tinted">
          All couriers
        </Link>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="card">
        <div className="courier-month-nav">
          <button
            type="button"
            className="button plain"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))
            }
          >
            ←
          </button>
          <strong>{monthLabel(month)}</strong>
          <button
            type="button"
            className="button plain"
            onClick={() =>
              setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))
            }
          >
            →
          </button>
        </div>
        <MonthCalendar
          month={month}
          renderCell={(cell) => {
            const iso = isoDate(cell.date);
            const shift = byDate.get(iso);
            const tappable =
              iso >= todayIso &&
              (!shift ||
                shift.status === "PLANNED" ||
                shift.status === "CONFIRMED");
            return (
              <button
                type="button"
                className="courier-day"
                disabled={!tappable || busy}
                onClick={() => onDayTap(cell.date)}
                aria-label={`${iso}${shift ? ` — ${STATUS_LABEL[shift.status]}` : ""}`}
              >
                <span className="courier-day-num">{cell.date.getDate()}</span>
                {shift ? (
                  <span
                    className={`courier-day-dot ${STATUS_DOT[shift.status]}`}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          }}
        />
        <div className="courier-legend muted">
          <span>
            <span className="courier-day-dot is-planned" /> Planned
          </span>
          <span>
            <span className="courier-day-dot is-confirmed" /> Confirmed
          </span>
          <span>
            <span className="courier-day-dot is-ended" /> Ended
          </span>
          <span>
            <span className="courier-day-dot is-missed" /> Missed
          </span>
        </div>
      </div>

      {pending?.kind === "plan" && courierId ? (
        <ReasonModal
          title="Plan this day"
          message={`Schedule ${courier?.fullName ?? "this courier"} for ${pending.date}? They will be asked to confirm it on the day.`}
          confirmLabel={busy ? "Planning…" : "Plan day"}
          busy={busy}
          onConfirm={() =>
            void run(
              () =>
                api.planningPlanDay({
                  courierUserId: courierId,
                  date: pending.date,
                }),
              "Could not plan the day",
            )
          }
          onClose={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === "cancel" ? (
        <ReasonModal
          title="Cancel this planned day"
          message={`Remove ${pending.shift.date} from ${courier?.fullName ?? "this courier"}'s calendar?`}
          confirmLabel={busy ? "Cancelling…" : "Cancel day"}
          destructive
          busy={busy}
          onConfirm={() =>
            void run(
              () => api.planningUpdateShift(pending.shift.id, { cancel: true }),
              "Could not cancel the day",
            )
          }
          onClose={() => setPending(null)}
        />
      ) : null}
    </section>
  );
}
